import React, { memo, useMemo } from 'react';
import CustomTooltip, { TOOLTIP_DURATION, TOOLTIP_POSITION } from '../Controls/CustomTooltip/CustomTooltip';
import { ProductWarningIcon } from './components/ProductWarningIcon/ProductWarningIcon';
import { categoryWarning } from './helpers/categoryWarning';
import { dealItemsInEditPageWarning, dealItemsInListWarning } from './helpers/dealItemsWarning';
import { mealItemsInEditPageWarning, mealItemsInListWarning } from './helpers/mealItemsWarning';
import { IProductV4 } from 'src/types/product';
import { IDeal, IDealItem, IMeal, IMealItem } from 'src/types/combo';
import './style.scss';

export const ProductWarningIconType = {
  TRIANGLE: 'triangle',
  CIRCLE: 'circle',
} as const;
type TProductWarningIconType = TValueOf<typeof ProductWarningIconType>;

export const ProductWarningType = {
  CATEGORY_IN_LIST: 'categoryInList',
  CATEGORY_IN_EDIT_PAGE: 'categoryInEditPage',
  //
  DEAL_ITEM_GLOBAL: 'dealItemsInListGlobal',
  DEAL_ITEM_BRANCH: 'dealItemsInListBranch',
  DEAL_ITEM_IN_EDIT_PAGE: 'dealItemsInEditPage',
  //
  MEAL_ITEM_GLOBAL: 'mealItemsInListGlobal',
  MEAL_ITEM_BRANCH: 'mealItemsInListBranch',
  MEAL_ITEM_IN_EDIT_PAGE: 'mealItemsInEditPage',
} as const;
export type TProductWarningType = TValueOf<typeof ProductWarningType>;

export interface IProductWarning {
  message: string;
  maxWidth?: number;
}

export interface IProductWarningProps {
  type?: TProductWarningType;
  combo?: IDeal | IMeal;
  product?: IProductV4;
  comboItem?: IProductV4 | IDealItem | IMealItem;
  comboItemOrderTypeWarning?: boolean;
  //
  iconType?: TProductWarningIconType;
  iconSize?: number;
  marginLeft?: number; // in bootstrap styles, step by 5
  position?: TOOLTIP_POSITION;
}

export const ProductWarning: React.FC<IProductWarningProps> = memo((props: IProductWarningProps): JSX.Element | null => {
  const { marginLeft = 3, position = TOOLTIP_POSITION.FIXED } = props;

  const warning = useMemo((): IProductWarning | null => {
    if ((!props.product && !props.combo && !props.comboItem) || props.product?.is_delete || props.combo?.is_delete) {
      return null;
    }

    switch (props.type) {
      // category
      case ProductWarningType.CATEGORY_IN_LIST:
        return categoryWarning({ type: props.type, product: props.product, combo: props.combo });
      case ProductWarningType.CATEGORY_IN_EDIT_PAGE:
        return categoryWarning({ type: props.type, product: props.product, combo: props.combo });
      // deal items
      case ProductWarningType.DEAL_ITEM_IN_EDIT_PAGE:
        return dealItemsInEditPageWarning({ comboItem: props.comboItem, comboItemOrderTypeWarning: props.comboItemOrderTypeWarning });
      case ProductWarningType.DEAL_ITEM_GLOBAL:
      case ProductWarningType.DEAL_ITEM_BRANCH:
        return dealItemsInListWarning({ type: props.type, combo: props.combo });
      // meal items
      case ProductWarningType.MEAL_ITEM_IN_EDIT_PAGE:
        return mealItemsInEditPageWarning({ comboItem: props.comboItem, comboItemOrderTypeWarning: props.comboItemOrderTypeWarning });
      case ProductWarningType.MEAL_ITEM_GLOBAL:
      case ProductWarningType.MEAL_ITEM_BRANCH:
        return mealItemsInListWarning({ type: props.type, combo: props.combo });

      default:
        return null;
    }
  }, [props.type, props.product, props.combo, props.comboItem, props.comboItemOrderTypeWarning]);

  if (!warning) return null;

  return (
    <div className={`ml-${marginLeft}`}>
      <CustomTooltip message={warning.message} maxWidth={warning.maxWidth} position={position} duration={TOOLTIP_DURATION.EXTRA_LARGE}>
        <ProductWarningIcon />
      </CustomTooltip>
    </div>
  );
});
